import { useState, useEffect, useRef } from "react";
import { Search, FileText } from "lucide-react";
import api, { inr } from "../lib/api";

// Reusable OA Number search/select. Typing searches existing OAs; picking one calls onPick(oa).
// Optionally controlled via value/onText so callers can also use it as a plain filter input.
export default function OASearchSelect({ onPick, value, onText, placeholder = "Search O.A. Number…", testid = "oa-search", autoFocus = false }) {
  const [inner, setInner] = useState("");
  const q = value !== undefined ? value : inner;
  const setQ = (v) => { onText ? onText(v) : setInner(v); };
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const debRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/oas", { params: q ? { oaNumber: q } : {} });
        setRows((data || []).slice(0, 10));
      } catch { setRows([]); }
      setLoading(false);
    }, 220);
    return () => clearTimeout(debRef.current);
  }, [q, open]);

  const pick = (oa) => { setOpen(false); onPick?.(oa); };

  return (
    <div className="relative w-full" ref={wrapRef}>
      <div className="relative">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          className="input pl-9 font-mono"
          value={q}
          placeholder={placeholder}
          autoFocus={autoFocus}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          data-testid={testid}
        />
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-80 overflow-y-auto" data-testid={`${testid}-dropdown`}>
          {loading && <div className="px-3 py-2 text-xs text-slate-400">Loading…</div>}
          {!loading && rows.length === 0 && <div className="px-3 py-3 text-sm text-slate-400" data-testid={`${testid}-empty`}>No O.A. found{q ? ` for "${q}"` : ""}.</div>}
          {rows.map((o) => (
            <button key={o.id} type="button" className="w-full text-left px-3 py-2.5 hover:bg-amber-50 border-b border-slate-50 last:border-0 flex items-center gap-3"
              onClick={() => pick(o)} data-testid={`${testid}-opt-${o.oaNumber}`}>
              <FileText className="h-4 w-4 text-amber-600 shrink-0" />
              <span className="min-w-0">
                <span className="block font-mono font-semibold text-sm text-slate-900">{o.oaNumber}</span>
                <span className="block text-xs text-slate-500 truncate">{o.customerName} · {inr(o.netTotal)}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
